/**
 * `AnimatedText` — the banner a lost level throws across the middle of the
 * screen, and `TextAnimator`, the per-character animation that plays it in.
 *
 * ## Where it sits, and who switches it on
 *
 * `AnimatedText` is the seventh child of `Gameplay.unity`'s Canvas and is
 * `m_IsActive: 0` there, because `GameplayController` is what enables it:
 *
 *     OnLevelFailed(e):                       // __0x211d880.c
 *         EventBus.Publish(new TimeScaleChangedEvent(1f));
 *         busController.ToggleInput(false);
 *         ...
 *         if (e.Reason == FailReason.Restart) return;          // :83
 *         soundService.Play(SoundTypeEnum.LevelFail);
 *         splineController.PlayFailCrashAnimation(cb);
 *         textAnimator.gameObject.SetActive(true);
 *         textAnimator.Initialize(e.Reason == OutOfMoves ? <literal A> : <literal B>);
 *         textAnimator.Play();
 *
 * so pressing Restart is silent — it returns before any of this — and the fail
 * popup only opens once **both** the crash and the text have finished.
 * `OnLevelLoaded` switches the node back off.
 *
 * ❓ **Two strings and which one.** Both literals are dropped by Ghidra, *and*
 * the `FailReason` handed to `LevelFailedEvent` is dropped at both publishers
 * (`QueueController.CheckFailCondition`:294 and
 * `SplineController.<CheckFailConditions>d__88`:103), so even the branch is a
 * guess. The port shows the out-of-moves message because that is what its one
 * loss condition means; both facts are declared rather than smoothed over.
 *
 * ## The animation, which *is* readable
 *
 * Every tuning field of `TextAnimator` is a `[SerializeField]`, so the scene's
 * values went with the component — but the class's own initialisers survive in
 * the constructor (🔍 `TextAnimator$$.ctor__0x2143f08.c`), which writes:
 *
 *     0x30 (8 bytes) = 0x3ee666663d0f5c29
 *         -> 0x30 delayBetweenChars      = 0x3d0f5c29 = 0.035
 *         -> 0x34 charAnimationDuration  = 0x3ee66666 = 0.45
 *     0x38 (8 bytes) = 0xc17000003fb9999a
 *         -> 0x38 maxPopScale            = 0x3fb9999a = 1.45
 *         -> 0x3C tiltAngle              = 0xc1700000 = -15
 *     0x40 yLiftAmount                   = 0x41700000 = 15
 *     0x48 startSpacingCompression       = 0x3dcccccd = 0.1
 *
 * `enableSpacingExpand` (0x44) is never written, so it is false and the spacing
 * branch of `Play` is not taken.
 *
 * `Play` (🔍 `__0x213dae8.c`) then builds, per character, inserted at
 * `index * delayBetweenChars`:
 *
 *     DOFadeChar(0, 0)                                  // instant
 *     Append(DOScaleChar(maxPopScale, dur * 0.35))
 *     Append(DOScaleChar(1f,          dur * 0.65))
 *     Insert(DORotateChar(zero,       dur))
 *     Append(DOOffsetChar(0, yLift, 0, dur * 0.5))
 *     Insert(DOFadeChar(1f,           dur * 0.15))
 *
 * with `Initialize` having set every character to `SetCharScale(Vector3.zero)`
 * first. ❓ The eases are gone with every other `SetEase` argument; the two the
 * decomp passes a constant to (`0x3f99999a` = 1.2) look like an `OutBack`
 * overshoot, which is what the CSS below approximates.
 */

/** 🔍 `TextAnimator..ctor` — see the header for the byte-level reading. */
export const TEXT_ANIMATOR = {
  delayBetweenChars: 0.035,
  charAnimationDuration: 0.45,
  maxPopScale: 1.45,
  tiltAngle: -15,
  yLiftAmount: 15,
  startSpacingCompression: 0.1,
  enableSpacingExpand: false,
}

/**
 * ❓ PLACEHOLDER — the two strings `OnLevelFailed` chooses between.
 *
 * Recorded in `GAMEPLAY-CHECKLIST.md` §10. Nothing in the repo can supply them:
 * they live in `global-metadata.dat`, and `reference/gameplay.mp4` never loses.
 */
export const FAIL_TEXT = {
  /** `e.Reason == FailReason.OutOfMoves`. */
  outOfMoves: 'OUT OF MOVES',
  /** Anything else that is not `Restart`. */
  other: 'LEVEL FAILED',
}

/** How long the whole banner takes for `count` characters. */
export function bannerSeconds(count: number): number {
  if (count <= 0) return 0
  return (count - 1) * TEXT_ANIMATOR.delayBetweenChars + TEXT_ANIMATOR.charAnimationDuration
}

export class AnimatedText {
  private readonly root: HTMLElement

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div')
    this.root.id = 'animatedText'
    this.root.hidden = true
    parent.append(this.root)
  }

  /** `SetActive(true)` + `Initialize(text)` + `Play()`, in that order. */
  play(text: string): void {
    this.root.replaceChildren()
    let index = 0
    for (const ch of text) {
      const span = document.createElement('span')
      // A space still takes a slot in TMP's character list, so it still consumes
      // a `delayBetweenChars` — dropping it would run the tail of the word early.
      span.textContent = ch === ' ' ? ' ' : ch
      span.style.animationDelay = `${index * TEXT_ANIMATOR.delayBetweenChars}s`
      this.root.append(span)
      index++
    }
    this.root.hidden = index === 0

    // TMP's auto-size is what keeps a long fail message inside the 400 x
    // (width - 160) rect; `m_fontSize` and the auto-size bounds went with the
    // component, so the port fits it the same way after laying it out.
    this.root.style.setProperty('--fit', '1')
    const room = this.root.clientWidth
    const wanted = this.root.scrollWidth
    if (room > 0 && wanted > room) {
      this.root.style.setProperty('--fit', String(room / wanted))
    }
  }

  /** `GameplayController.OnLevelLoaded` — `textAnimator.gameObject.SetActive(false)`. */
  hide(): void {
    this.root.hidden = true
    this.root.replaceChildren()
  }

  get visible(): boolean {
    return !this.root.hidden
  }
}
